import { useState, useCallback } from 'react';
import { api } from '../lib/api';

interface SurpriseMeProps {
  onNavigate: (page: string, data?: any) => void;
}

export default function SurpriseMe({ onNavigate }: SurpriseMeProps) {
  const [open, setOpen] = useState(false);
  const [story, setStory] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [flipping, setFlipping] = useState(false);

  const fetchSurprise = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.stories.surprise();
      setStory(data.story);
    } catch (err) {
      console.error('Surprise failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleOpen = async () => {
    setOpen(true);
    await fetchSurprise();
  };

  const handleTryAgain = async () => {
    setFlipping(true);
    await fetchSurprise();
    setTimeout(() => setFlipping(false), 500);
  };

  const coverGradients = [
    'from-purple-600 to-blue-600',
    'from-rose-600 to-pink-600',
    'from-emerald-600 to-teal-600',
    'from-amber-600 to-orange-600',
  ];

  return (
    <>
      <button
        onClick={handleOpen}
        className="fixed bottom-6 right-6 z-40 w-14 h-14 bg-ink-600 hover:bg-ink-500 rounded-full shadow-lg shadow-ink-600/30 flex items-center justify-center transition-all hover:scale-110 group"
        title="Surprise Me!"
      >
        <span className="text-2xl group-hover:animate-spin">✦</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-gray-950/90 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-gray-900 border border-gray-800 rounded-2xl max-w-md w-full p-8 text-center" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-ink-400 mb-6">✦ Surprise Me!</h2>

            {loading ? (
              <div className="py-16">
                <div className="w-10 h-10 border-3 border-ink-400 border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : story ? (
              <div className={`transition-all duration-500 ${flipping ? 'scale-95 opacity-50 rotate-y-180' : ''}`}>
                <div className={`aspect-[6/9] max-w-[200px] mx-auto rounded-xl overflow-hidden bg-gradient-to-br ${coverGradients[story.id % coverGradients.length]} mb-4`}>
                  {story.coverUrl ? (
                    <img src={story.coverUrl} alt={story.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center p-4">
                      <span className="text-white/90 text-lg font-serif font-bold text-center">{story.title}</span>
                    </div>
                  )}
                </div>
                <h3 className="text-xl font-bold">{story.title}</h3>
                <p className="text-gray-400 text-sm mt-1">by {story.author?.username}</p>
                <div className="flex gap-2 justify-center mt-3">
                  {story.genre && (
                    <span className="px-2 py-0.5 bg-ink-600/20 text-ink-400 text-xs rounded-full">{story.genre}</span>
                  )}
                </div>
                {story.synopsis && (
                  <p className="text-gray-400 text-sm mt-3 line-clamp-2">{story.synopsis.split('.')[0]}.</p>
                )}

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={handleTryAgain}
                    className="flex-1 px-4 py-2.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm font-medium transition-colors"
                  >
                    Not for me — try again
                  </button>
                  <button
                    onClick={() => { onNavigate('story', { id: story.id, slug: story.slug }); setOpen(false); }}
                    className="flex-1 px-4 py-2.5 bg-ink-600 hover:bg-ink-500 rounded-lg text-sm font-medium transition-colors"
                  >
                    Read First Chapter
                  </button>
                </div>
              </div>
            ) : (
              <div className="py-16 text-gray-500">
                <p>No stories available yet. Be the first to write one!</p>
              </div>
            )}

            <button
              onClick={() => setOpen(false)}
              className="mt-4 text-sm text-gray-500 hover:text-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
}
